import {
  getDefaultPaginationOffset,
  REACT_APP_GENETIC_REPORTS_DELAY_DAYS,
  REACT_APP_GENETIC_REPORTS_END_DATE,
  REACT_APP_GENETIC_REPORTS_START_DATE,
  REACT_APP_HISTO_CYTO_DELAY_DAYS,
  REACT_APP_HISTO_CYTO_END_DATE,
  REACT_APP_HISTO_CYTO_START_DATE,
  REACT_APP_RADIOLOGY_DELAY_DAYS,
  REACT_APP_RADIOLOGY_END_DATE,
  REACT_APP_RADIOLOGY_START_DATE,
} from '@shared/configs/configs/config';
import {
  MRRP_MEDICAL_REPORTS_COLLECTION_PATH,
  MRRP_MEDICAL_REPORTS_DETAILS_PATH,
  MRRP_MEDICAL_REPORTS_LISTING_PATH,
  MRRP_MEDICAL_REPORTS_NEW_SELECT_INSTITUTION_PATH,
  MRRP_MEDICAL_REPORTS_PARTICULARS_PATH,
  MRRP_MEDICAL_REPORTS_PATH,
  MRRP_MEDICAL_REPORTS_PATIENT_NOT_FOUND_PATH,
  MRRP_MEDICAL_REPORTS_REPORTS_DETAILS_PATH,
  MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_COMPLETED_PATH,
  MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_DD_RECEIPT_PATH,
  MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_PATH,
  MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_STATUS_PATH,
  MRRP_MEDICAL_REPORTS_REQUEST_SUMMARY_PATH,
  MRRP_MEDICAL_REPORTS_TNC_DETAILS_PATH,
  MRRP_MEDICAL_REPORTS_TNC_PATH,
  MRRP_MEDICAL_REPORTS_VIEW_SELECT_INSTITUTION_PATH,
} from '@shared/constants/paths';
import type { ResizeFilePayload } from '@shared/utils/image';

import type { Day } from '@features/HealthierSG/types/healthierSG.types';

export const DATE_FORMAT = 'DD MMM YYYY';
export const DATE_DAY_TIME_FORMAT = 'DD MMM YYYY (ddd), hh:mm A';
export const DATE_TIME_SECONDS_FORMAT = 'DD MMM YYYY, HH:mm:ss';

export const PATH_PUBLIC_URL = '/public';

export const SUCCESS_MESSAGE = 'Success';

export const CONNECTION_ERROR_BASE64_SESSION_KEY = 'connectionErrorBase64';

export const SOURCE_MYINFO = 'MyInfo';
export const FULL_NAME_QUERY = 'fullName';
export const MID_QUERY = 'mid';
export const DISPLAY_NAME_QUERY = 'displayName';
export const QUERY_PARAMS_REDIRECT_TO = 'redirectTo';
export const HEALTHIERSG_FAQ_URL = 'https://ask.gov.sg/healthiersg';
export const WHAT_IS_HEALTHIER_SG_URL = 'https://www.healthiersg.gov.sg/about/what-is-healthier-sg';

export const SwitchProfileType = {
  ALL: 'ALL',
  CHILDREN: 'CHILDREN',
  FAMILY: 'FAMILY',
  FILTERED: 'FILTERED',
  SELF: 'SELF',
} as const;

export const ProfileUpdateMode = {
  ALL: 'All',
  ContactNo: 'ContactNo',
} as const;

export type SwitchProfileTypeType = (typeof SwitchProfileType)[keyof typeof SwitchProfileType];

export const ResourceType = {
  ACCESS_LOG: 'ACCESS_LOG',
  ALL: 'ALL',
  APPOINTMENTS: 'APPOINTMENTS',
  APPOINTMENTS_TRXN: 'APPOINTMENTS_TRXN',
  APPOINTMENTS_VIEW: 'APPOINTMENTS_VIEW',
  CAREPLAN: 'CAREPLAN',
  CHAS_DETAILS: 'CHAS_DETAILS',
  COMPOSITION: 'COMPOSITION',
  COMPOSITION_INDICATOR: 'COMPOSITION_INDICATOR',
  DIABETES_RISK_ASSESSMENT: 'DIABETES_RISK_ASSESSMENT',
  DIAGNOSES_PROBLEMS: 'DIAGNOSES_PROBLEMS',
  DISABILITY_DETAIL: 'DISABILITY_DETAIL',
  DISABILITY_LIST: 'DISABILITY_LIST',
  DISCHARGE_SUMMARY: 'DISCHARGE_SUMMARY',
  ENROLMENT: 'ENROLMENT',
  HEALTH_SCREENING: 'HEALTH_SCREENING',
  HEALTHIER_SG: 'HEALTHIER_SG',
  IMMUNISATIONS: 'IMMUNISATIONS',
  LAB_RESULTS: 'LAB_RESULTS',
  MEDICAL_ALERT_AND_ADR_DRUG_ALLERGY: 'MEDICAL_ALERT_AND_ADR_DRUG_ALLERGY',
  MEDICAL_REPORTS_CERTIFICATE: 'MEDICAL_REPORTS_CERTIFICATE',
  MEDICATIONREFILL: 'MEDICATIONREFILL',
  MEDICATIONS: 'MEDICATIONS',
  OPT_OUT: 'OPT_OUT',
  PAYMENTS: 'PAYMENTS',
  PRESCRIPTION_RECORDS: 'PRESCRIPTION_RECORDS',
  RADIOLOGY_NUCLEAR_MEDICINE_REPORTS: 'RADIOLOGY_NUCLEAR_MEDICINE_REPORTS',
  SCREENING: 'SCREENING',
  SCREENING_SUMMARY: 'SCREENING_SUMMARY',
  VITALS: 'VITALS',
} as const;

// Date formats
export const DATE_DISPLAY = 'DD MMM YYYY';
export const DATETIME_DISPLAY1 = 'DD MMM YYYY, hh:mma';
export const DAY_DATE_DISPLAY = 'ddd, MMM DD';
export const FIN_NRIC_CODE = 'U0008';
export const NEHR_OPT_OUT_CODE = 'OPT_OUT';

export const SYSTEM_ERROR_MESSAGE_PREFIX = 'Request failed with status code';

export const CONSENT_HPB_URL = 'https://consent.hpb.gov.sg/';

export const CARE_RECIPIENT_ACCESS_URL =
  'https://support.healthhub.sg/hc/en-us/sections/15755746612121-Caregiver-and-Care-Recipient-Access';

export const RelatedPeopleStatus = {
  ACCEPTED: 'Accepted',
  APPROVED: 'Approved',
  PENDING: 'Request Pending',
  REMOVED_BY_CAREGIVER: 'Removed by Caregiver',
} as const;

export type RelatedPeopleStatusType = (typeof RelatedPeopleStatus)[keyof typeof RelatedPeopleStatus];

export const RELATIONSHIP_OPTIONS = [
  {
    label: 'Parent',
    value: 'Parent',
  },
  {
    label: 'Spouse',
    value: 'Spouse',
  },
  {
    label: 'Sibling',
    value: 'Sibling',
  },
  {
    label: 'Child',
    value: 'Child',
  },
  {
    label: 'Friend',
    value: 'Friend',
  },
  {
    label: 'Other Relative',
    value: 'Other Relative',
  },
  {
    label: 'Others',
    value: 'Others',
  },
];

export const RESIZE_AVATAR_DEFAULT_PAYLOAD: ResizeFilePayload = {
  compressFormat: 'png',
  maxHeight: 200,
  maxWidth: 200,
  outputType: 'file',
  quality: 70,
};

export const SourceCodeType = {
  CHILDREN: 'CH',
  FAMILY_FRIEND: 'FH',
  PERSONAL: 'PH',
} as const;

export type SourceCodeTypeType = (typeof SourceCodeType)[keyof typeof SourceCodeType];

export const Cluster = {
  HPB: 'HPB',
  NHG: 'NHG',
  NUHS: 'NUHS',
  SHS: 'SHS',
} as const;

export const OrgCode = {
  HPB_SDC: 'HPB_SDC',
  HPB_SHC: 'HPB_SHC',
} as const;

export const Gender = {
  FEMALE: 'F',
  MALE: 'M',
} as const;

export type GenderType = (typeof Gender)[keyof typeof Gender];

export const GenderValue = {
  Female: 'Female',
  Male: 'Male',
} as const;

export const NUMBER_OF_PAGINATION_ITEMS = getDefaultPaginationOffset();

export const OPTIONAL_ENVIRONMENT_VARIABLES_KEYS = [
  REACT_APP_HISTO_CYTO_START_DATE,
  REACT_APP_HISTO_CYTO_END_DATE,
  REACT_APP_HISTO_CYTO_DELAY_DAYS,
  REACT_APP_RADIOLOGY_START_DATE,
  REACT_APP_RADIOLOGY_END_DATE,
  REACT_APP_RADIOLOGY_DELAY_DAYS,
  REACT_APP_GENETIC_REPORTS_START_DATE,
  REACT_APP_GENETIC_REPORTS_END_DATE,
  REACT_APP_GENETIC_REPORTS_DELAY_DAYS,
];

export const TERM_OF_USES_TITLE_CODE = 'Terms.Of.Use.Title';
export const TERM_OF_USES_CODE = 'Terms.Of.Use';
export const IS_MOBILE_CALLBACK_URL = 'isMobileCallbackUrl';

export const MRRP_ROUTES = [
  MRRP_MEDICAL_REPORTS_PATH,
  MRRP_MEDICAL_REPORTS_VIEW_SELECT_INSTITUTION_PATH,
  MRRP_MEDICAL_REPORTS_LISTING_PATH,
  MRRP_MEDICAL_REPORTS_DETAILS_PATH,
  MRRP_MEDICAL_REPORTS_NEW_SELECT_INSTITUTION_PATH,
  MRRP_MEDICAL_REPORTS_TNC_PATH,
  MRRP_MEDICAL_REPORTS_TNC_DETAILS_PATH,
  MRRP_MEDICAL_REPORTS_PATIENT_NOT_FOUND_PATH,
  MRRP_MEDICAL_REPORTS_REPORTS_DETAILS_PATH,
  MRRP_MEDICAL_REPORTS_PARTICULARS_PATH,
  MRRP_MEDICAL_REPORTS_COLLECTION_PATH,
  MRRP_MEDICAL_REPORTS_REQUEST_SUMMARY_PATH,
  MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_PATH,
  MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_STATUS_PATH,
  MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_DD_RECEIPT_PATH,
  MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_COMPLETED_PATH,
];

export const SourceApp = {
  HB: 'HB',
  HH: 'HH',
  NCA: 'NCA',
  NMA: 'NMA',
  'SS-NMA': 'SS-NMA',
} as const;

export const SourceAppView = {
  HB: 'HB',
  HH_MOBILE: 'HH_MOBILE',
  HH_WEB: 'HH_WEB',
  NCA: 'NCA',
  NMA: 'NMA',
  'SS-NMA': 'NMA',
} as const;

export type SourceAppViewType = (typeof SourceAppView)[keyof typeof SourceAppView];

export const LayoutType = {
  COMMON: 'COMMON',
  MOBILE: 'MOBILE',
  PORTAL: 'PORTAL',
  SHARED: 'SHARED',
  STANDARD: 'STANDARD',
} as const;

export type LayoutTypeType = (typeof LayoutType)[keyof typeof LayoutType];

export const PageType = {
  PRIVATE: 'PRIVATE',
  PUBLIC: 'PUBLIC',
} as const;

export type PageTypeType = (typeof PageType)[keyof typeof PageType];

export const SourceSystem = {
  NGEMR: 'NGEMR',
  OAS: 'OAS',
};

export const APPOINTMENT_SLOTS_PER_PAGE = 25;

export const APP_STORE_URL = {
  android: 'https://play.google.com/store/apps/details?id=sg.gov.hpb.healthhub',
  ios: 'https://apps.apple.com/sg/app/healthhub-sg/id1034200875',
};

export const USER_PROFILE_DEFAULT_NAME = 'Myself';

export const DEFAULT_DAYS: Day[] = [
  { Name: 'Mon', Value: 'Monday', DayOfWeek: 2, IsSelected: false, HasExercise: false },
  { Name: 'Tue', Value: 'Tuesday', DayOfWeek: 3, IsSelected: false, HasExercise: false },
  { Name: 'Wed', Value: 'Wednesday', DayOfWeek: 4, IsSelected: false, HasExercise: false },
  { Name: 'Thu', Value: 'Thursday', DayOfWeek: 5, IsSelected: false, HasExercise: false },
  { Name: 'Fri', Value: 'Friday', DayOfWeek: 6, IsSelected: false, HasExercise: false },
  { Name: 'Sat', Value: 'Saturday', DayOfWeek: 7, IsSelected: false, HasExercise: false },
  { Name: 'Sun', Value: 'Sunday', DayOfWeek: 1, IsSelected: false, HasExercise: false },
];
export const DEFAULT_TEXT_SCALE = 1;
